import React from 'react';
import { Alert, Grid, Spin, Tag, Typography } from 'antd';
import { ClockCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { formatMeasurementTimestamp, formatUtcTimestamp } from '../../utils/measurementTime';
import { useMeasurementReport } from './measurementReport/MeasurementReportContext';
import { MeasurementSectionShell, buildMeasurementSectionMeta } from './measurementReport/MeasurementSectionShell';

const { Text } = Typography;

interface CommonSectionProps {
    section: any;
}

const formatLocalPose = (pose?: Record<string, any> | null) => {
    const x = Number(pose?.x);
    const y = Number(pose?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    const frame = typeof pose?.frameId === 'string' && pose.frameId ? pose.frameId : 'map';
    const system = typeof pose?.coordinateSystem === 'string' && pose.coordinateSystem
        ? pose.coordinateSystem
        : 'local';
    return `${system} ${frame}: x ${x.toFixed(2)}, y ${y.toFixed(2)}`;
};

export const ReportMeasurementInfo: React.FC<CommonSectionProps> = ({ section }) => {
    const screens = Grid.useBreakpoint();
    const isMobile = screens.md === false;
    const {
        measurementId,
        measurement,
        loading,
        error,
        areaHierarchy,
        areaHierarchyLoading,
    } = useMeasurementReport();
    const sectionTitle = section?.title || 'Measurement info';
    const sectionMeta = buildMeasurementSectionMeta(measurement);

    if (!measurementId) {
        return (
            <MeasurementSectionShell title={sectionTitle}>
                <Alert message="Select a measurement to view details." type="info" showIcon />
            </MeasurementSectionShell>
        );
    }

    if (loading) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Spin tip="Loading measurement..." />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (error || !measurement) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <Alert message={error || 'Measurement not found'} type="error" showIcon />
            </MeasurementSectionShell>
        );
    }

    const measurementCode = measurement.measurementCode || `Measurement ${measurement.id}`;
    const measuredAt = formatMeasurementTimestamp(measurement.measuredAt, measurement, { seconds: false });
    const createdAt = formatUtcTimestamp(measurement.createdAt);
    const hierarchyNodes = areaHierarchy.length > 0 ? areaHierarchy : (measurement.area ? [measurement.area] : []);
    const localPose = formatLocalPose(measurement.localPose);

    return (
        <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
            <div className={`report-measurement-info-summary ${isMobile ? 'report-measurement-info-summary--stacked' : ''}`}>
                <div className="report-measurement-key-stat report-measurement-key-stat--code">
                    <span className="report-measurement-key-stat-label">Measurement code</span>
                    <span className="report-measurement-key-stat-value report-measurement-code">{measurementCode}</span>
                </div>
                <div className="report-measurement-key-stat">
                    <span className="report-measurement-key-stat-label">Device</span>
                    <Tag icon={<ThunderboltOutlined />} color="blue" className="report-measurement-device-tag">
                        {measurement.device?.label || 'Unknown'}
                    </Tag>
                </div>
                <div className="report-measurement-key-stat">
                    <span className="report-measurement-key-stat-label">Measured at</span>
                    <span className="report-measurement-key-stat-value">
                        <ClockCircleOutlined /> {measuredAt}
                    </span>
                </div>
                <div className="report-measurement-key-stat">
                        <span className="report-measurement-key-stat-label">Uploaded at</span>
                    <span className="report-measurement-key-stat-value">
                        <ClockCircleOutlined /> {createdAt}
                    </span>
                </div>
            </div>

            <div className="report-measurement-info-grid">
                <div className="report-measurement-info-card report-measurement-info-card--wide">
                    <span className="report-measurement-info-label">Area hierarchy</span>
                    {areaHierarchyLoading ? (
                        <Text type="secondary">Loading hierarchy…</Text>
                    ) : hierarchyNodes.length > 0 ? (
                        <div className="report-measurement-chip-row">
                            {hierarchyNodes.map((area: any, index: number) => (
                                <Tag key={area.id} color={index === hierarchyNodes.length - 1 ? 'green' : 'default'}>
                                    {area.name}
                                    {area.areaType?.name ? ` · ${area.areaType.name}` : ''}
                                </Tag>
                            ))}
                        </div>
                    ) : (
                        <Text type="secondary">Not assigned</Text>
                    )}
                </div>

                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Pattern</span>
                    {measurement.pattern ? (
                        <div className="report-measurement-chip-row">
                            <Tag color="blue">{measurement.pattern.name}</Tag>
                            {measurement.pattern.patternType ? (
                                <Text type="secondary">{measurement.pattern.patternType}</Text>
                            ) : null}
                        </div>
                    ) : (
                        <Text type="secondary">Not assigned</Text>
                    )}
                </div>

                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Pattern point</span>
                    {measurement.patternPoint ? (
                        <Tag color="cyan">
                            {measurement.patternPoint.label || `Point #${measurement.patternPoint.pointIndex}`}
                        </Tag>
                    ) : (
                        <Text type="secondary">Not assigned</Text>
                    )}
                </div>

                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Mission</span>
                    {measurement.mission ? (
                        <Tag color="purple">{measurement.mission.name}</Tag>
                    ) : (
                        <Text type="secondary">Not assigned</Text>
                    )}
                </div>

                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Mission point</span>
                    {measurement.missionPoint ? (
                        <Tag color="magenta">
                            {measurement.missionPoint.label || `Point #${measurement.missionPoint.pointIndex}`}
                        </Tag>
                    ) : (
                        <Text type="secondary">Not assigned</Text>
                    )}
                </div>

                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Local position</span>
                    {localPose ? <Tag color="geekblue">{localPose}</Tag> : <Text type="secondary">Not recorded</Text>}
                </div>
            </div>
        </MeasurementSectionShell>
    );
};

export default ReportMeasurementInfo;
